import { useDeferredValue, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { getAccount } from '@/api';
import { liveEventDetailsQuery, liveEventsTablesQuery, EMPTY_LIVE_EVENTS_FILTER } from '@/api/queries';
import { selectOutcomeForEvent, useBetslipTicket, withBetslipSelectedState } from '@/betslip';
import {
  POPULAR_MARKET_GROUP,
  filterEventMarkets,
  formatOutcomeLabel,
  isNextGoalMarket,
  mapEventDetails,
} from '@/domain/event-details';
import { isLiveStreamConfigured } from '@/domain/lmt';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { translateMarketLabel, formatMarketTitle } from '@/i18n/market-labels';
import { translateOutcomeLabel, sanitizeOutcomeDisplay } from '@/i18n/outcome-labels';
import { applyLiveStatisticsPatch, resolveEventRowLiveInfo } from '@/domain/live-statistics';
import { translatePeriodLabel } from '@/i18n/period-label';
import { isLiveEventNotFoundError } from '@/live/live-event-finished';
import { useLiveEventState, useLiveFeed, useLiveFeedRevision } from '@/live/LiveFeedProvider';
import { toSlug } from '@/shell/sports-menu-utils';
import {
  Button,
  EmptyState,
  LiveEventDetailNav,
  LiveEventHeader,
  LiveEventsRail,
  MarketCard,
  MarketToolbar,
  Skeleton,
} from '@/ui';

const liveRouteApi = getRouteApi('/live');

export interface LiveEventDetailsProps {
  eventId: string;
  /** Loader already saw a live-details 404 (event no longer on live). */
  initiallyFinished?: boolean;
}

export function LiveEventDetails({ eventId, initiallyFinished = false }: LiveEventDetailsProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    sport: sportSlug,
    tournament: tournamentSlug,
    media: mediaSearch,
  } = liveRouteApi.useSearch();
  const { selectedLanguage, isLoadingTranslations } = useTranslations();
  const account = getAccount();
  const feedType = account?.FeedType;
  const betslipTicket = useBetslipTicket();
  const liveFeed = useLiveFeed();
  const numericEventId = Number(eventId);
  const liveState = useLiveEventState(
    Number.isFinite(numericEventId) ? numericEventId : null,
  );
  const socketFinished = liveState?.finished === true;
  const query = useQuery({
    ...liveEventDetailsQuery(eventId),
    enabled: !initiallyFinished && !socketFinished,
  });
  const finished =
    initiallyFinished || socketFinished || isLiveEventNotFoundError(query.error);
  const [selectedTabId, setSelectedTabId] = useState(POPULAR_MARKET_GROUP);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);

  const sportName = query.data?.Sport ?? '';
  const railFilters = useMemo(
    () =>
      sportName
        ? {
            Sport: sportName,
            Categories: [] as string[],
            Tournaments: [] as string[],
            EventIds: [] as number[],
            Take: 0,
          }
        : null,
    [sportName],
  );

  const { data: liveTables } = useQuery({
    ...liveEventsTablesQuery(railFilters ?? EMPTY_LIVE_EVENTS_FILTER),
    enabled: railFilters !== null && !finished,
  });

  const resolveMarketLabel = useMemo(() => {
    void selectedLanguage;
    void isLoadingTranslations;
    return (key: string) => translateMarketLabel(key, i18n, feedType);
  }, [feedType, i18n, isLoadingTranslations, selectedLanguage]);

  const resolveOutcomeLabel = useMemo(() => {
    void selectedLanguage;
    void isLoadingTranslations;
    return (code: string, specifier?: string, marketKey?: string, marketGroup?: string) => {
      const translated = translateOutcomeLabel(code, i18n, feedType);
      const marketLabel = marketKey ? translateMarketLabel(marketKey, i18n, feedType) : undefined;
      if (isNextGoalMarket(marketKey ?? '', marketGroup, marketLabel)) {
        return sanitizeOutcomeDisplay(translated);
      }
      return formatOutcomeLabel(translated, specifier);
    };
  }, [feedType, i18n, isLoadingTranslations, selectedLanguage]);

  const resolvePeriodLabel = useMemo(() => {
    void selectedLanguage;
    void isLoadingTranslations;
    return (key: string) => translatePeriodLabel(key, t, i18n);
  }, [i18n, isLoadingTranslations, selectedLanguage, t]);

  const view = useMemo(
    () =>
      query.data
        ? mapEventDetails(query.data, {
            translatePeriod: resolvePeriodLabel,
            statisticsPatch: liveState?.statisticsPatch,
            oddsPatch: liveState?.oddsPatch,
            eventMetaPatch: liveState?.eventMetaPatch,
            getOutcomeFlash: (flashKey: string) => liveState?.flashes?.get(flashKey),
          })
        : null,
    [liveState, query.data, resolvePeriodLabel],
  );

  const railEventIds = useMemo(
    () => (liveTables ?? []).flatMap((table) => (table.Events ?? []).map((e) => e.EventId)),
    [liveTables],
  );
  const railRevision = useLiveFeedRevision(railEventIds);

  const railEvents = useMemo(() => {
    void railRevision;
    if (!liveTables) return [];
    // Keep feed order — do not move the selected event to the front.
    return liveTables.flatMap((table) =>
      (table.Events ?? []).map((event) => {
        const liveInfo = resolveEventRowLiveInfo(
          applyLiveStatisticsPatch(event, liveFeed.getStatisticsPatch(event.EventId)),
          resolvePeriodLabel,
        );
        return {
          eventId: event.EventId,
          home: event.Home ?? 'Home',
          away: event.Away ?? 'Away',
          homeScore: liveInfo.homeScore,
          awayScore: liveInfo.awayScore,
          clock: liveInfo.clock,
          liveClockState: liveInfo.liveClockState,
          translatePeriod: resolvePeriodLabel,
          hasLiveStream: Boolean(event.LiveStream) && isLiveStreamConfigured(account),
        };
      }),
    );
  }, [account, liveFeed, liveTables, railRevision, resolvePeriodLabel]);

  const tabs = useMemo(() => {
    if (!view) return [];
    return view.tabs.map((tab) => ({
      id: tab.id,
      label:
        tab.id === POPULAR_MARKET_GROUP
          ? t('All', { defaultValue: 'All' })
          : resolveMarketLabel(tab.code),
    }));
  }, [resolveMarketLabel, t, view]);

  const visibleMarkets = useMemo(() => {
    if (!view) return [];
    const tabId = view.tabs.some((tab) => tab.id === selectedTabId)
      ? selectedTabId
      : POPULAR_MARKET_GROUP;
    const grouped = filterEventMarkets(view.markets, tabId);
    const needle = deferredSearchQuery.trim().toLowerCase();
    if (!needle) return grouped;
    return grouped.filter((market) => resolveMarketLabel(market.key).toLowerCase().includes(needle));
  }, [deferredSearchQuery, resolveMarketLabel, selectedTabId, view]);

  if (finished) {
    return (
      <EmptyState
        icon="details"
        title={t('Event finished', { defaultValue: 'Event finished' })}
        description={t('This live event has ended.', {
          defaultValue: 'This live event has ended.',
        })}
        action={
          <Button
            variant="secondary"
            onClick={() => {
              void navigate({
                to: '/live',
                search: { sport: sportSlug, tournament: tournamentSlug, media: undefined },
              });
            }}
          >
            {t('Back to live', { defaultValue: 'Back to live' })}
          </Button>
        }
      />
    );
  }

  if (query.isPending) {
    return <LiveEventDetailsSkeleton />;
  }

  if (query.isLoadingError || !view) {
    return (
      <EmptyState
        icon="warning"
        title={t('Could not load event', { defaultValue: 'Could not load event' })}
        description={t('Try again in a moment.', { defaultValue: 'Try again in a moment.' })}
      />
    );
  }

  const alternativeLabel = t('Alternative Options', { defaultValue: 'Alternative Options' });

  return (
    <div className="flex flex-col gap-3">
      <LiveEventDetailNav
        league={view.header.league}
        categoryName={view.header.category}
        categoryId={view.header.categoryId}
        tournamentName={view.header.tournament}
        tournamentId={view.header.tournamentId}
        crestSrc={view.header.crestSrc}
        sport={sportSlug ?? (view.sport ? toSlug(view.sport) : undefined)}
        tournament={tournamentSlug}
      />

      <LiveEventsRail
        events={railEvents}
        selectedEventId={view.eventId}
        onSelectEvent={(nextEventId) => {
          void navigate({
            to: '/live/$eventId',
            params: { eventId: String(nextEventId) },
            search: { sport: sportSlug, tournament: tournamentSlug, media: undefined },
          });
        }}
      />

      <LiveEventHeader
        home={view.header.home}
        away={view.header.away}
        clock={view.header.clock}
        liveClockState={view.header.liveClockState}
        translatePeriod={resolvePeriodLabel}
        homeScore={view.header.homeScore}
        awayScore={view.header.awayScore}
        statistics={view.statistics}
        eventId={view.eventId}
        sport={view.sport ?? sportName}
        matchTrackerEnabled={view.matchTrackerEnabled}
        theSportsId={view.theSportsId}
        liveStream={view.liveStream}
        initialMediaMode={mediaSearch === 'stream' ? 'stream' : undefined}
      />

      <MarketToolbar
        tabs={tabs}
        selectedTabId={view.tabs.some((tab) => tab.id === selectedTabId)
          ? selectedTabId
          : POPULAR_MARKET_GROUP}
        onSelectTab={setSelectedTabId}
        searchOpen={searchOpen}
        searchQuery={searchQuery}
        onToggleSearch={() =>
          setSearchOpen((open) => {
            if (open) setSearchQuery('');
            return !open;
          })
        }
        onSearchChange={setSearchQuery}
      />

      {visibleMarkets.length === 0 ? (
        <EmptyState
          icon="details"
          title={t('No markets', { defaultValue: 'No markets' })}
          description={t('No markets match this filter.', {
            defaultValue: 'No markets match this filter.',
          })}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {visibleMarkets.map((market) => (
            <MarketCard
              key={market.id}
              title={formatMarketTitle(resolveMarketLabel(market.key), {
                hasAlternativeLines: market.lines.length > 1,
                specifier:
                  market.lines.find((line) => line.main)?.specifier ?? market.lines[0]?.specifier,
              })}
              lines={market.lines.map((line) => ({
                ...line,
                outcomes: withBetslipSelectedState(
                  betslipTicket,
                  view.eventId,
                  line.outcomes.map((outcome) => ({
                    ...outcome,
                    marketName: resolveMarketLabel(market.key),
                    outcome: resolveOutcomeLabel(
                      outcome.outcome,
                      line.specifier,
                      market.key,
                      market.group,
                    ),
                  })),
                ),
              }))}
              alternativeLabel={alternativeLabel}
              onSelectOutcome={(outcome) => {
                if (!query.data) return;
                selectOutcomeForEvent(query.data, outcome, account);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function LiveEventDetailsSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton kind="line" className="h-5 w-48" />
      <Skeleton kind="block" className="h-[72px] w-full" />
      <Skeleton kind="block" className="h-80 w-full" />
      <Skeleton kind="line" className="h-9 w-full" />
      <Skeleton kind="block" className="h-24 w-full" />
      <Skeleton kind="block" className="h-24 w-full" />
    </div>
  );
}
